import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Info, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface RarityConfig {
  rarity: string;
  min_appearance_pct: number;
  max_appearance_pct: number;
  min_win_pct: number;
  max_win_pct: number;
  updated_at: string;
}

interface PacingMetric {
  campaign_name: string;
  rarity: string;
  current_appearance_chance: number;
  current_win_chance: number;
  is_behind: boolean;
  is_ahead: boolean;
  calculated_at: string;
}

const DropsProbabilityAdmin = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [rarityConfig, setRarityConfig] = useState<RarityConfig[]>([]);
  const [pacingMetrics, setPacingMetrics] = useState<PacingMetric[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // Refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      // Load global rarity config
      const { data: config, error: configError } = await supabase
        .from('drop_rarity_config')
        .select('*')
        .order('rarity');

      if (configError) throw configError;
      setRarityConfig(config || []);

      // Load recent pacing metrics
      const { data: metrics, error: metricsError } = await supabase.rpc(
        'get_recent_pacing_metrics'
      );

      if (!metricsError && metrics) {
        setPacingMetrics(metrics);
      }

      setLoading(false);
    } catch (error: any) {
      console.error('Failed to load probability data:', error);
      toast({
        title: 'Error loading data',
        description: error.message,
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const formatPercent = (value: number) => {
    if (value >= 1) return `${value.toFixed(2)}%`;
    if (value >= 0.01) return `${value.toFixed(3)}%`;
    return `${value.toFixed(5)}%`;
  };

  const getRarityColor = (rarity: string) => {
    const colors: Record<string, string> = {
      common: 'text-gray-400',
      uncommon: 'text-green-400',
      rare: 'text-blue-400',
      epic: 'text-purple-400',
      legendary: 'text-orange-400',
    };
    return colors[rarity.toLowerCase()] || 'text-gray-400';
  };

  const getRarityBadge = (rarity: string) => {
    const badges: Record<string, string> = {
      common: 'bg-gray-500/20 text-gray-300',
      uncommon: 'bg-green-500/20 text-green-300',
      rare: 'bg-blue-500/20 text-blue-300',
      epic: 'bg-purple-500/20 text-purple-300',
      legendary: 'bg-orange-500/20 text-orange-300',
    };
    return badges[rarity.toLowerCase()] || badges.common;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/drops/admin')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Drops Admin
          </Button>

          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold">Drop Probabilities</h1>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="p-4 mb-6 bg-primary/5 border-primary/20">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-primary mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold mb-1">How Probabilities Work</h3>
              <p className="text-sm text-muted-foreground">
                The system uses <strong>2 layers of randomization</strong>: (1) Appearance - will
                drop show in feed? (2) Rarity - which tier? Probabilities are clamped to
                global min/max bands below, but auto-adjust based on campaign pacing every 5
                minutes.
              </p>
            </div>
          </div>
        </Card>

        {/* Global Rarity Bands */}
        <Card className="p-6 mb-6">
          <h2 className="text-xl font-bold mb-4">Global Probability Bands</h2>
          <p className="text-sm text-muted-foreground mb-6">
            These are the min/max bounds that all campaigns respect. Pacing controller adjusts
            odds within these ranges.
          </p>

          <div className="grid gap-4">
            {rarityConfig.map((config) => (
              <div
                key={config.rarity}
                className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold uppercase ${getRarityBadge(
                      config.rarity
                    )}`}
                  >
                    {config.rarity}
                  </span>
                </div>

                <div className="flex gap-8">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">Appearance Range</div>
                    <div className="text-sm font-mono">
                      {formatPercent(config.min_appearance_pct)} -{' '}
                      {formatPercent(config.max_appearance_pct)}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-muted-foreground mb-1">
                      Win Range (legacy - now 100%)
                    </div>
                    <div className="text-sm font-mono text-muted-foreground line-through">
                      {formatPercent(config.min_win_pct)} - {formatPercent(config.max_win_pct)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 p-4 rounded-lg bg-green-500/10 border border-green-500/20">
            <p className="text-sm text-green-300">
              <strong>Note:</strong> Win ranges are legacy - the system now awards drops at{' '}
              <strong>100% win rate</strong> when clicked (no gambling mechanics). Only appearance
              odds matter.
            </p>
          </div>
        </Card>

        {/* Active Campaign Pacing */}
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4">Active Campaign Pacing (Real-Time)</h2>
          <p className="text-sm text-muted-foreground mb-6">
            Current adjusted probabilities for each active campaign. Updates every 5 minutes via
            cron job.
          </p>

          {pacingMetrics.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p className="mb-2">No active campaigns with pacing data yet.</p>
              <p className="text-sm">Create and approve a campaign to see live pacing metrics.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(
                pacingMetrics.reduce((acc, metric) => {
                  if (!acc[metric.campaign_name]) acc[metric.campaign_name] = [];
                  acc[metric.campaign_name].push(metric);
                  return acc;
                }, {} as Record<string, PacingMetric[]>)
              ).map(([campaignName, metrics]) => (
                <div key={campaignName} className="border border-border rounded-lg p-4">
                  <h3 className="font-semibold mb-3">{campaignName}</h3>

                  <div className="space-y-2">
                    {metrics.map((metric) => (
                      <div
                        key={`${campaignName}-${metric.rarity}`}
                        className="flex items-center justify-between p-3 rounded bg-muted/20"
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className={`px-2 py-1 rounded text-xs font-semibold uppercase ${getRarityBadge(
                              metric.rarity
                            )}`}
                          >
                            {metric.rarity}
                          </span>

                          {metric.is_behind && (
                            <span className="flex items-center gap-1 text-xs text-orange-400">
                              <TrendingUp className="w-3 h-3" />
                              Behind Schedule
                            </span>
                          )}
                          {metric.is_ahead && (
                            <span className="flex items-center gap-1 text-xs text-blue-400">
                              <TrendingDown className="w-3 h-3" />
                              Ahead of Schedule
                            </span>
                          )}
                          {!metric.is_behind && !metric.is_ahead && (
                            <span className="flex items-center gap-1 text-xs text-green-400">
                              <Minus className="w-3 h-3" />
                              On Track
                            </span>
                          )}
                        </div>

                        <div className="flex gap-6">
                          <div className="text-right">
                            <div className="text-xs text-muted-foreground">Appearance</div>
                            <div className="text-sm font-mono font-semibold">
                              {formatPercent(metric.current_appearance_chance * 100)}
                            </div>
                          </div>

                          <div className="text-right">
                            <div className="text-xs text-muted-foreground">Claim (if seen)</div>
                            <div className="text-sm font-mono font-semibold text-green-400">
                              100%
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-2 text-xs text-muted-foreground text-right">
                    Last updated: {new Date(metrics[0].calculated_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Query Reference */}
        <Card className="p-6 mt-6 bg-muted/30">
          <h3 className="font-semibold mb-3">Admin SQL Queries</h3>
          <div className="space-y-4 text-sm font-mono">
            <div>
              <div className="text-muted-foreground mb-1">View global bands:</div>
              <code className="block bg-background p-3 rounded text-xs">
                SELECT * FROM drop_rarity_config ORDER BY rarity;
              </code>
            </div>

            <div>
              <div className="text-muted-foreground mb-1">View live pacing:</div>
              <code className="block bg-background p-3 rounded text-xs">
                SELECT * FROM drop_pacing_metrics WHERE calculated_at {'>'} NOW() - INTERVAL '1
                hour';
              </code>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DropsProbabilityAdmin;
